import { resend } from "@/lib/resend";

type OrderEmailItem = {
  productNameSnapshot: string;
  quantity: number;
};

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatPrice(value: string) {
  return `${Number(value).toFixed(2)} €`;
}

function renderOrderItems(items: OrderEmailItem[]) {
  return items
    .map(
      (item) => `
        <tr>
          <td style="padding:12px 0;border-bottom:1px solid #dcc2c8;font-size:14px;line-height:1.6;color:#2f1f24;">
            ${escapeHtml(item.productNameSnapshot)}
          </td>
          <td style="padding:12px 0;border-bottom:1px solid #dcc2c8;text-align:right;font-size:14px;line-height:1.6;color:#2f1f24;white-space:nowrap;">
            × ${item.quantity}
          </td>
        </tr>
      `
    )
    .join("");
}

type SendOrderConfirmationParams = {
  to: string;
  orderNumber: string;
  customerFirstName?: string | null;
  total: string;
  items: OrderEmailItem[];
};

export async function sendOrderConfirmationEmail({
  to,
  orderNumber,
  customerFirstName,
  total,
  items,
}: SendOrderConfirmationParams) {
  const itemsHtml = renderOrderItems(items);

  const firstName = customerFirstName?.trim() || "Bonjour";
  const safeFirstName = escapeHtml(firstName);

  const result = await resend.emails.send({
    from: process.env.EMAIL_FROM as string,
    to,
    subject: `Confirmation de commande ${orderNumber}`,
    html: `
      <div style="margin:0;background:#f7edef;padding:24px 12px;font-family:Georgia,'Times New Roman',serif;color:#2b1c20;">
        <div style="max-width:680px;margin:0 auto;overflow:hidden;border:1px solid #dcc2c8;border-radius:28px;background:#fffdfd;box-shadow:0 12px 32px rgba(60,28,36,0.08);">
          <div style="padding:34px 28px 26px 28px;background:#fbf1f3;border-bottom:1px solid #dcc2c8;">
            <p style="margin:0 0 14px 0;font-size:12px;letter-spacing:0.22em;text-transform:uppercase;color:#9b4b5c;">
              Jour J Création
            </p>
            <h1 style="margin:0;font-size:34px;line-height:1.15;font-weight:600;color:#2b1c20;">
              Merci pour votre commande
            </h1>
            <p style="margin:16px 0 0 0;font-size:16px;line-height:1.8;color:#4e373d;">
              ${safeFirstName}, votre commande <strong>${escapeHtml(orderNumber)}</strong> a bien été confirmée. Merci de soutenir un univers imaginé et préparé avec soin.
            </p>
          </div>

          <div style="padding:30px 36px;">
            <div style="border:1px solid #dcc2c8;border-radius:22px;background:#ffffff;padding:22px 24px;">
              <p style="margin:0 0 14px 0;font-size:12px;letter-spacing:0.18em;text-transform:uppercase;color:#9b4b5c;">
                Votre commande
              </p>
              <table role="presentation" style="width:100%;border-collapse:collapse;">
                ${itemsHtml}
              </table>
              <div style="margin-top:18px;padding-top:18px;border-top:1px solid #dcc2c8;display:flex;justify-content:space-between;gap:16px;align-items:center;">
                <span style="font-size:14px;line-height:1.6;color:#5a434a;">Total</span>
                <strong style="font-size:20px;line-height:1.2;color:#2b1c20;">${formatPrice(total)}</strong>
              </div>
            </div>

            <div style="margin-top:20px;border:1px solid #dcc2c8;border-radius:22px;background:#f8eef0;padding:20px 24px;">
              <p style="margin:0 0 10px 0;font-size:12px;letter-spacing:0.18em;text-transform:uppercase;color:#9b4b5c;">
                Et maintenant ?
              </p>
              <p style="margin:0;font-size:15px;line-height:1.8;color:#4e373d;">
                Votre commande est enregistrée et préparée avec attention. Vous pourrez retrouver son suivi dans votre espace client. Chaque création est pensée avec une touche artisanale, douce et singulière.
              </p>
            </div>
          </div>

          <div style="padding:0 36px 34px 36px;">
            <div style="border-top:1px solid #dcc2c8;padding-top:20px;">
              <p style="margin:0;font-size:15px;line-height:1.8;color:#4e373d;">
                Merci pour votre confiance,<br />
                <strong style="color:#2b1c20;">Jour J Création</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    `,
  });

  console.log("📨 Résultat Resend :", result);

  if (result.error) {
    throw new Error(
      result.error.message || "Erreur lors de l'envoi de l'email"
    );
  }
}

type SendAdminOrderNotificationParams = {
  orderNumber: string;
  customerFirstName?: string | null;
  customerLastName?: string | null;
  customerEmail: string;
  customerPhone?: string | null;
  shippingAddressText?: string | null;
  total: string;
  items: OrderEmailItem[];
};

export async function sendAdminOrderNotificationEmail({
  orderNumber,
  customerFirstName,
  customerLastName,
  customerEmail,
  customerPhone,
  shippingAddressText,
  total,
  items,
}: SendAdminOrderNotificationParams) {
  const itemsHtml = renderOrderItems(items);

  const customerName =
    [customerFirstName, customerLastName].filter(Boolean).join(" ") || "Cliente";
  const safeCustomerName = escapeHtml(customerName);
  const safeCustomerEmail = escapeHtml(customerEmail);
  const safeCustomerPhone = escapeHtml(customerPhone || "Non renseigné");
  const safeShippingAddress = escapeHtml(shippingAddressText || "Non renseignée");

  const result = await resend.emails.send({
    from: process.env.EMAIL_FROM as string,
    to: process.env.EMAIL_ADMIN as string,
    subject: `Nouvelle commande ${orderNumber}`,
    html: `
      <div style="margin:0;background:#f7edef;padding:24px 12px;font-family:Georgia,'Times New Roman',serif;color:#2b1c20;">
        <div style="max-width:720px;margin:0 auto;overflow:hidden;border:1px solid #dcc2c8;border-radius:28px;background:#fffdfd;box-shadow:0 12px 32px rgba(60,28,36,0.08);">
          <div style="padding:34px 28px 26px 28px;background:#fbf1f3;border-bottom:1px solid #dcc2c8;">
            <p style="margin:0 0 14px 0;font-size:12px;letter-spacing:0.22em;text-transform:uppercase;color:#9b4b5c;">
              Jour J Création · Admin
            </p>
            <h1 style="margin:0;font-size:32px;line-height:1.15;font-weight:600;color:#2b1c20;">
              Nouvelle commande reçue
            </h1>
            <p style="margin:16px 0 0 0;font-size:16px;line-height:1.8;color:#4e373d;">
              Une nouvelle commande vient d’être enregistrée sous la référence <strong>${escapeHtml(orderNumber)}</strong>.
            </p>
          </div>

          <div style="padding:30px 36px;">
            <div style="display:grid;gap:18px;">
              <div style="border:1px solid #dcc2c8;border-radius:22px;background:#ffffff;padding:22px 24px;">
                <p style="margin:0 0 14px 0;font-size:12px;letter-spacing:0.18em;text-transform:uppercase;color:#9b4b5c;">
                  Informations cliente
                </p>
                <p style="margin:0 0 10px 0;font-size:15px;line-height:1.7;color:#2f1f24;"><strong>Nom :</strong> ${safeCustomerName}</p>
                <p style="margin:0 0 10px 0;font-size:15px;line-height:1.7;color:#2f1f24;"><strong>Email :</strong> ${safeCustomerEmail}</p>
                <p style="margin:0;font-size:15px;line-height:1.7;color:#2f1f24;"><strong>Téléphone :</strong> ${safeCustomerPhone}</p>
              </div>

              <div style="border:1px solid #dcc2c8;border-radius:22px;background:#ffffff;padding:22px 24px;">
                <p style="margin:0 0 14px 0;font-size:12px;letter-spacing:0.18em;text-transform:uppercase;color:#9b4b5c;">
                  Adresse de livraison
                </p>
                <pre style="white-space:pre-wrap;margin:0;font-family:Georgia,'Times New Roman',serif;font-size:15px;line-height:1.8;color:#2f1f24;">${safeShippingAddress}</pre>
              </div>

              <div style="border:1px solid #dcc2c8;border-radius:22px;background:#ffffff;padding:22px 24px;">
                <p style="margin:0 0 14px 0;font-size:12px;letter-spacing:0.18em;text-transform:uppercase;color:#9b4b5c;">
                  Détail de la commande
                </p>
                <table role="presentation" style="width:100%;border-collapse:collapse;">
                  ${itemsHtml}
                </table>
                <div style="margin-top:18px;padding-top:18px;border-top:1px solid #dcc2c8;display:flex;justify-content:space-between;gap:16px;align-items:center;">
                  <span style="font-size:14px;line-height:1.6;color:#5a434a;">Total</span>
                  <strong style="font-size:20px;line-height:1.2;color:#2b1c20;">${formatPrice(total)}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `,
  });

  console.log("📨 Résultat Resend admin :", result);

  if (result.error) {
    throw new Error(
      result.error.message || "Erreur lors de l'envoi de l'email admin"
    );
  }
}